import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Zap, ArrowRight, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { formatApiError } from '@/lib/api';
import { BoostaBolt } from '@/components/BoostaBolt';

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success('Welcome back.');
      nav('/dashboard');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-boosta-ink flex items-center justify-center px-4 relative">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="absolute inset-0 grain" />
      <div className="absolute top-6 left-6">
        <Link to="/" className="flex items-center gap-2"><BoostaBolt size={28} animate/><span className="font-display font-bold text-3d">Boosta</span></Link>
      </div>
      <div className="relative w-full max-w-md">
        <div className="glass rounded-3xl p-8 md:p-10">
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan mb-3">Welcome back</div>
          <h1 className="font-display font-black text-3xl tracking-tight">Sign in to your command centre</h1>
          <form onSubmit={submit} className="mt-7 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-white/45 mb-2 block">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2"/>
                <input type="email" required value={email} onChange={e=>setEmail(e.target.value)} data-testid="login-email"
                  className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-3 py-3 text-sm focus:border-boosta-cyan/50 outline-none transition" placeholder="you@company.com"/>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs uppercase tracking-wider text-white/45 block">Password</label>
                <Link to="/forgot-password" className="text-xs text-boosta-cyan hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
              </div>
              <div className="relative">
                <Lock className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2"/>
                <input type={showPw ? 'text' : 'password'} required value={password} onChange={e=>setPassword(e.target.value)} data-testid="login-password"
                  className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-11 py-3 text-sm focus:border-boosta-cyan/50 outline-none transition" placeholder="••••••••"/>
                <button type="button" onClick={()=>setShowPw(v=>!v)} data-testid="login-toggle-password"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-boosta-cyan transition" aria-label={showPw?'Hide password':'Show password'}>
                  {showPw ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                </button>
              </div>
            </div>
            <button disabled={busy} type="submit" data-testid="login-submit" className="w-full btn-primary rounded-xl py-3.5 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
              {busy ? 'Signing in…' : <>Sign in <ArrowRight className="w-4 h-4"/></>}
            </button>
          </form>
          <div className="flex items-center gap-3 my-6 text-[10px] uppercase tracking-[0.2em] text-white/30">
            <div className="flex-1 h-px bg-white/10"/> Or <div className="flex-1 h-px bg-white/10"/>
          </div>
          <button onClick={googleSignIn} data-testid="login-google" className="w-full btn-ghost rounded-xl py-3 text-sm font-medium">
            Continue with Google
          </button>
          <div className="mt-7 text-center text-sm text-white/45">
            New here?{' '}<Link to="/register" className="text-boosta-cyan hover:underline">Create an account</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
